import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { getNotices } from '@/lib/api';
import { formatRelativeDay } from '@/lib/format';
import type { Session } from '@/lib/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Empty, Loading, PageTitle, StateBadge } from '@/components/shell';

/**
 * Tutto quello che il Comune ha pubblicato, dal più recente.
 *
 * A differenza dell'app — che vede solo gli avvisi vivi, ed è giusto così —
 * qui compaiono anche gli scaduti e i ritirati. Un avviso sbagliato va tolto
 * dall'app, ma deve restare consultabile da chi l'ha mandato: è la differenza
 * fra un ritiro e una cancellazione.
 */
export function Storico({ session }: { session: Session }) {
  const notices = useQuery({
    queryKey: ['notices', session.istat],
    queryFn: () => getNotices(session.istat, 200),
  });

  if (notices.isLoading) return <Loading what="Carico lo storico" />;

  if (notices.isError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>Non riesco a leggere lo storico</AlertTitle>
        <AlertDescription>Ricarica la pagina.</AlertDescription>
      </Alert>
    );
  }

  const rows = notices.data ?? [];

  return (
    <>
      <PageTitle>Storico</PageTitle>

      {rows.length === 0 ? (
        <Empty>Questo Comune non ha ancora pubblicato nessun avviso.</Empty>
      ) : (
        <Card className="overflow-hidden py-0">
          <CardContent className="divide-y px-0">
            {rows.map((n) => (
              <Link
                key={n.id}
                to={`/avviso/${n.id}`}
                className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-accent"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StateBadge state={n.stato} />
                    {n.revokes_id && <Badge variant="outline">Revoca</Badge>}
                    {!n.push_requested && (
                      <Badge variant="outline">Senza notifica</Badge>
                    )}
                  </div>
                  <p className="mt-1.5 text-[16px] font-semibold leading-snug text-ink">
                    {n.title}
                  </p>
                  <p className="tnum mt-0.5 text-[13px] text-ink4">
                    {formatRelativeDay(n.published_at)}
                    {n.actor ? ` · ${n.actor.replace(/^console:/, '')}` : ''}
                  </p>
                </div>
                <ChevronRight className="size-5 shrink-0 text-ink4" />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}
